import React, { useContext, useState } from 'react';
import { Coord } from '@99designs/browser';
import { AnnotationDisplayComponentType, CreateAnnotation, CreateAnnotationResult } from '../types';

export type BaseAnnotation<A> = A & {
    id: string;
    content: string;
    position: Coord;
};

export interface AnnotationContextValue<A> {
    canAnnotate: boolean;
    annotations: BaseAnnotation<A>[];
    setAnnotations: React.Dispatch<React.SetStateAction<BaseAnnotation<A>[]>>;
    addAnnotations: (newAnnotations: BaseAnnotation<A>[]) => void;
    removeAnnotation: (annotationId: string) => void;
    newAnnotationCoord: Coord | null;
    setNewAnnotationCoord: React.Dispatch<React.SetStateAction<Coord | null>>;
    lastAnnotation: BaseAnnotation<A> | null;
    setLastAnnotation: React.Dispatch<React.SetStateAction<BaseAnnotation<A> | null>>;
    AnnotationDisplay: AnnotationDisplayComponentType;
    createAnnotation: CreateAnnotation<A>;
    createAnnotationResult: CreateAnnotationResult;
}

export interface AnnotationProviderProps<A> {
    children: React.ReactNode;
    AnnotationDisplay: AnnotationDisplayComponentType;
    createAnnotation: CreateAnnotation<A>;
    createAnnotationResult: CreateAnnotationResult;
    canAnnotate?: boolean;
}

export interface AnnotationContext<A> {
    AnnotationProvider: (props: AnnotationProviderProps<A>) => React.ReactElement;
    useAnnotation: () => AnnotationContextValue<A>;
}

export function createAnnotationContext<A>(): AnnotationContext<A> {
    const context = React.createContext<AnnotationContextValue<A> | null>(null);

    function AnnotationProvider(props: AnnotationProviderProps<A>) {
        const {
            children,
            createAnnotationResult,
            AnnotationDisplay,
            createAnnotation,
            canAnnotate = false,
        } = props;

        const [annotations, setAnnotations] = useState<BaseAnnotation<A>[]>([]);
        const [newAnnotationCoord, setNewAnnotationCoord] = useState<Coord | null>(null);
        const [lastAnnotation, setLastAnnotation] = useState<BaseAnnotation<A> | null>(null);

        /**
         * Function to add an array of annotations to state. This function also removes duplicates before commiting it to the state
         */
        const addAnnotations = (newAnnotations: BaseAnnotation<A>[]): void => {
            setAnnotations((prevAnnotations) => {
                const uniqueIds: Record<string, true> = {};
                const dedupedAnnotations = [...prevAnnotations, ...newAnnotations].reduce(
                    (prevResult, annotation) => {
                        if (uniqueIds[annotation.id]) {
                            return [...prevResult];
                        }
                        uniqueIds[annotation.id] = true;
                        return [...prevResult, annotation];
                    },
                    []
                );
                return dedupedAnnotations;
            });
        };

        const removeAnnotation = (annotationId: string): void =>
            setAnnotations((prevAnnotations) =>
                prevAnnotations.filter((annotation) => annotation.id !== annotationId)
            );

        return (
            <context.Provider
                value={{
                    canAnnotate,
                    annotations,
                    addAnnotations,
                    setAnnotations,
                    newAnnotationCoord,
                    removeAnnotation,
                    setNewAnnotationCoord,
                    lastAnnotation,
                    setLastAnnotation,
                    createAnnotation,
                    createAnnotationResult,
                    AnnotationDisplay,
                }}
            >
                {children}
            </context.Provider>
        );
    }

    function useAnnotation(): AnnotationContextValue<A> {
        const value = useContext(context);

        if (!value) {
            throw new Error('useAnnotation must be used within a AnnotationProvider');
        }

        return value;
    }

    return {
        AnnotationProvider,
        useAnnotation,
    };
}
